"use client";

import { Dialog, DialogContent, DialogTitle, DialogCloseButton } from "@shared/src/components/ui/dialog";
import { SocialLoginView } from "@shared/src/components/modules/auth/SocialLoginView";
import { useAuthStore, VIEW_CONFIG } from "@shared/src/stores/useAuthStore";
import { IoChevronBack } from "react-icons/io5";
import { SignInModalSkeleton } from "@shared/src/components/ui/common/skeletons/SignInModalSkeleton";
import { useWalletConnection } from "@shared/src/hooks/useWalletConnection";
import { ViewContent } from "@shared/src/components/modules/auth/signInViewContent";
import { useEffect, useState } from "react";
import useAuth from "@shared/src/hooks/auth/useAuth";
import { useAccount, useDisconnect } from "wagmi";
import { getEoaOrScaSigner } from "@shared/src/lib/auth";
import { WALLECTCONNECT_ICON } from "@shared/src/constants/tags";
import { useSigner } from "@account-kit/react";
import { TbSocial } from "react-icons/tb";
import { SiSocialblade } from "react-icons/si";
import { HiAtSymbol } from "react-icons/hi2";
import { useQuickTradeFlowStore } from "@shared/src/stores/useQuickTradeFlowStore";

interface ConnectWalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status?: "linking" | "signing_in";
}

function WalletIconSkeleton() {
  return <div className="w-10 h-10 rounded-2xl bg-surfaces-bg-secondary-darker animate-pulse" />;
}

function MobileView() {
  const {
    connectors,
    selectedView,
    isVerifying,
    handleBack,
    connectWallet,
    handleWalletConnectClick,
    handleImageError,
  } = useWalletConnection();

  const currentView = VIEW_CONFIG[selectedView];

  const walletIcon = useAuthStore(state => state.walletIcon);
  const accountLinkingStatus = useAuthStore(state => state.accountLinkingStatus);
  const walletName = useAuthStore(state => state.walletName);
  const installedWallets = connectors;
  const closeAndResetAuthModal = useAuthStore(state => state.closeAndResetAuthModal);
  const activeAccount = useAuthStore(state => state.activeAccount);

  const renderInitialView = () => (
    <>
      {/* Social Login Section */}
      <div className="w-full space-y-2 px-2">
        <p className="text-text-tertiary font-bold text-center text-xs">Social Login</p>
        <SocialLoginView />
      </div>

      {/* Only show wallets section if not verifying */}
      {!isVerifying && (
        <div className="w-full space-y-2 mt-2">
          <p className="text-text-tertiary font-bold text-center text-xs">Wallets</p>
          <div className="flex flex-wrap gap-2 items-center justify-center px-2">
            {/* WalletConnect Button */}
            <button
              key="walletconnect"
              onClick={handleWalletConnectClick}
              className="flex flex-col items-center justify-center p-1.5 rounded-xl border border-border-tertiary hover:opacity-75 transition-all duration-200"
            >
              <img
                src="/images/logos/WalletConnectLogo.svg"
                alt="WalletConnect"
                width={32}
                height={32}
                className="rounded-2xl"
                onError={() => handleImageError("walletconnect")}
              />
            </button>

            {installedWallets.map(
              wallet =>
                wallet && (
                  <button
                    key={wallet.uid}
                    onClick={() => connectWallet(wallet)}
                    className="flex flex-col items-center justify-center p-1.5 rounded-xl border border-border-tertiary hover:opacity-75 transition-all duration-200"
                  >
                    <img
                      src={wallet.icon?.trim() || "/images/logos/WalletConnectLogo.svg"}
                      alt={wallet.name || "Wallet"}
                      width={32}
                      height={32}
                      className="rounded-2xl"
                      onError={e => {
                        e.currentTarget.src = "/images/logos/WalletConnectLogo.svg";
                      }}
                    />
                    <span className="text-text-tertiary text-[10px]">Installed</span>
                  </button>
                ),
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="md:hidden flex flex-col h-full py-3 overflow-y-auto flex-shrink min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        {accountLinkingStatus ? (
          <button onClick={handleBack} className="text-text-tertiary group hover:text-text-primary flex-shrink-0">
            <IoChevronBack className="w-8 h-8 rounded-full p-1.5 group-hover:border border-surfaces-bg-tertiary" />
          </button>
        ) : (
          <div className="w-8 h-8 flex-shrink-0" />
        )}
        <DialogTitle className="font-unbounded text-base flex flex-col items-center font-semibold text-text-primary flex-shrink">
          {accountLinkingStatus === "initial" ? "" : currentView.title}
          {accountLinkingStatus === "linking" && (
            <p className="text-text-secondary mt-1 font-manrope text-xs">
              {`Please sign in with your ${activeAccount?.jwt ? "new" : "existing"} account to link it.`}
            </p>
          )}
        </DialogTitle>
        <DialogCloseButton onClose={() => closeAndResetAuthModal()} className="flex-shrink-0 w-8 h-8" />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2 items-center flex-1 flex-shrink min-h-0 overflow-y-auto">
        {selectedView === "social" && !isVerifying ? (
          renderInitialView()
        ) : (
          <ViewContent
            selectedView={selectedView}
            isVerifying={isVerifying}
            walletName={walletName}
            walletIcon={walletIcon}
          />
        )}
      </div>
    </div>
  );
}

function DesktopView() {
  const {
    connectors,
    selectedView,
    isVerifying,
    imageErrors,
    handleBack,
    handleImageError,
    connectWallet,
    setSelectedView,
    handleWalletConnectClick,
  } = useWalletConnection();

  const currentView = VIEW_CONFIG[selectedView];
  const installedWallets = connectors;
  const closeAndResetAuthModal = useAuthStore(state => state.closeAndResetAuthModal);
  const activeAccount = useAuthStore(state => state.activeAccount);
  const showModalSidebar = useAuthStore(state => state.showModalSidebar);
  const accountLinkingStatus = useAuthStore(state => state.accountLinkingStatus);

  const walletIcon = useAuthStore(state => state.walletIcon?.trim());
  const walletName = useAuthStore(state => state.walletName);

  const linkingHeading = (
    <div className="flex flex-col mt-2 mr-10 h-full text-left">
      <p className="text-text-secondary font-manrope text-small-2">
        {`Please sign in with your ${activeAccount?.jwt ? "new" : "existing"} account to link it.`}
      </p>
    </div>
  );

  // Determine the background color based on the selected view
  const getSelectedBgClass = () => {
    if (
      selectedView === "social" ||
      selectedView === "all" ||
      selectedView === "walletConnect" ||
      selectedView === "WalletConnect"
    ) {
      return "bg-surfaces-bg-secondary-darker";
    }
    return "bg-surfaces-bg-secondary-darker";
  };

  // Helper function to determine if an option is selected
  const isOptionSelected = (optionId: string) => {
    if (optionId === "walletconnect" || optionId === "WalletConnect") {
      return selectedView === "walletConnect" || selectedView === "WalletConnect";
    }
    return selectedView === optionId;
  };

  return (
    <div className="hidden md:flex w-full overflow-hidden transition-all duration-300 ease-in-out">
      {/* Left Side - using relative positioning and custom right padding */}
      {showModalSidebar && (
        <div className={`w-[45%] pl-4 ${selectedView ? "pr-0" : "pr-4"} flex flex-col py-10 relative`}>
          <DialogTitle className="font-unbounded text-h6 font-semibold text-text-primary mb-6">
            {accountLinkingStatus === "linking" ? "Link Account" : "Sign In"}
            {accountLinkingStatus === "linking" && linkingHeading}
          </DialogTitle>

          {/* Social Login Section - reduced margin bottom */}
          <div className="mb-3 relative">
            <button
              onClick={() => setSelectedView("social")}
              className={`w-full flex items-center gap-2 py-4 px-6 rounded-2xl hover:opacity-75 transition-all duration-300 ease-in-out ${
                selectedView === "social" ? "bg-surfaces-bg-secondary-darker  rounded-r-none" : ""
              }`}
            >
              <span>
                <HiAtSymbol width={200} height={0} />
              </span>

              <span className="text-text-primary text-small">Social Login</span>
            </button>
            {/* Extension div that appears when selected */}
            {selectedView === "social" && (
              <div
                className="absolute right-0 top-0 bottom-0 w-4 bg-surfaces-bg-secondary-darker transition-all duration-300 ease-in-out"
                style={{ transform: "translateX(100%)" }}
              ></div>
            )}
          </div>

          {/* Wallets Section - removed "Installed Wallets" header */}
          <div>
            <div className="space-y-0 max-h-[400px] overflow-y-auto pr-2">
              <div className="relative">
                <button
                  key={"walletconnect"}
                  onClick={handleWalletConnectClick}
                  className={`w-full flex items-center gap-2 py-4 px-6 rounded-2xl hover:opacity-75 transition-all duration-300 ease-in-out ${
                    isOptionSelected("walletconnect") ? "bg-surfaces-bg-secondary-darker  rounded-r-none" : ""
                  }`}
                >
                  <img
                    src={"/images/logos/WalletConnectLogo.svg"}
                    alt={"Wallet"}
                    width={40}
                    height={40}
                    className="rounded-2xl"
                    onError={() => handleImageError("walletconnect")}
                  />
                  <div className="flex flex-col items-start">
                    <span className="text-text-primary text-small">WalletConnect</span>
                    <span className="text-text-tertiary text-small"></span>
                  </div>
                </button>
                {/* Extension div that appears when selected */}
                {isOptionSelected("walletconnect") && (
                  <div
                    className="absolute right-0 top-0 bottom-0 w-4 bg-surfaces-bg-secondary-darker transition-all duration-300 ease-in-out"
                    style={{ transform: "translateX(100%)" }}
                  ></div>
                )}
              </div>

              {installedWallets.map(
                wallet =>
                  wallet && (
                    <div key={wallet.uid} className="relative">
                      <button
                        onClick={() => connectWallet(wallet)}
                        className={`w-full flex items-center gap-2 py-4 px-6 rounded-2xl hover:opacity-75 transition-all duration-300 ease-in-out ${
                          selectedView === wallet.id ? "bg-surfaces-bg-secondary-darker  rounded-r-none" : ""
                        }`}
                      >
                        {imageErrors[wallet.id] ? (
                          <WalletIconSkeleton />
                        ) : (
                          <img
                            src={wallet.icon?.trim() || "/images/logos/WalletConnectLogo.svg"}
                            alt={wallet.name || "Wallet"}
                            width={40}
                            height={40}
                            className="rounded-2xl"
                            onError={() => handleImageError(wallet.id)}
                          />
                        )}
                        <div className="flex flex-col items-start">
                          <span className="text-text-primary text-small">{wallet.name || "Wallet"}</span>
                          {/* Added "Installed" indicator */}
                          {wallet.isInstalled ? <span className="text-text-tertiary text-xs">Installed</span> : ""}
                        </div>
                      </button>
                      {/* Extension div that appears when selected */}
                      {selectedView === wallet.id && (
                        <div
                          className="absolute right-0 top-0 bottom-0 w-4 bg-surfaces-bg-secondary-darker transition-all duration-300 ease-in-out"
                          style={{ transform: "translateX(100%)" }}
                        ></div>
                      )}
                    </div>
                  ),
              )}

              <div className="relative">
                {/* Extension div that appears when selected */}
                {selectedView === "all" && (
                  <div
                    className="absolute right-0 top-0 bottom-0 w-4 bg-surfaces-bg-secondary-darker transition-all duration-300 ease-in-out"
                    style={{ transform: "translateX(100%)" }}
                  ></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right Side - added rounded corners and made background conditional */}
      <div
        className={` ${showModalSidebar ? "w-full" : "w-[55%]"} ${selectedView ? "pl-0" : "pl-6"} relative ${
          selectedView ? `${getSelectedBgClass()} rounded-5xl` : ""
        } transition-all duration-300 ease-in-out`}
      >
        <div className="py-10 px-6">
          <div className={`flex justify-between items-center mb-6`}>
            {/* TODO: @Jovells  Handle showing back button */}

            {accountLinkingStatus ? (
              <button onClick={handleBack} className="text-text-tertiary group hover:text-text-primary">
                <IoChevronBack className="w-10 h-10 rounded-full p-2 group-hover:border border-surfaces-bg-tertiary" />
              </button>
            ) : (
              <button className="opacity-0 text-text-tertiary group hover:text-text-primary">
                <IoChevronBack className="w-10 h-10 rounded-full p-2 group-hover:border border-surfaces-bg-tertiary" />
              </button>
            )}
            <DialogTitle className="font-unbounded text-h6 font-semibold text-text-primary capitalize">
              {accountLinkingStatus === "initial" ? "" : currentView.title}
            </DialogTitle>
            <DialogCloseButton onClose={() => closeAndResetAuthModal()} />
          </div>

          <div>
            <ViewContent
              selectedView={selectedView}
              isVerifying={isVerifying}
              walletIcon={walletIcon}
              walletName={walletName}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export type WalletSection = "social" | "walletConnect" | "installedWallets" | "allWallets";
export type SignInView = WalletSection | "emailVerification";

export type SignInModalProps = {
  view?: SignInView;
  email?: string;
  //passing an empty array will hide the nav. Leaving it out will show the default nav
  sections?: WalletSection[];
};

export function SignInModal({ view, email, sections }: SignInModalProps) {
  //TODO @Jovells continue
  const {
    modalOpen,
    setModalOpen,
    setIsVerifying,
    setEmail,
    setSelectedView,
    selectedView,
    setWalletStatus,
    justConnected,
    alchemyOauthPrepared,
    setAlchemyOauthPrepared,
  } = useAuthStore();
  const { authenticate, handleIxAuth } = useAuth();
  const showSignInModal = useQuickTradeFlowStore(state => state.showSignInModal);
  const setShowSignInModal = useQuickTradeFlowStore(state => state.setShowSignInModal);

  const isOpen = modalOpen || showSignInModal;
  const handleOpenChange = (open: boolean) => {
    console.log("SignInModal open change", open);
    setModalOpen(open);
    setShowSignInModal(open);
  };

  // Update verification status when `view` prop changes
  useEffect(() => {
    if (view === "emailVerification") {
      setIsVerifying(true);
    }
  }, [view, setIsVerifying]);

  // Update email in store when `email` prop changes
  useEffect(() => {
    if (email) {
      setEmail(email);
    }
  }, [email, setEmail]);

  const { connector } = useAccount();
  const { disconnect } = useDisconnect();
  const alchSigner = useSigner();

  const [showSkeleton, setShowSkeleton] = useState(!alchemyOauthPrepared);

  // Fade out skeleton and fade in content when prepared
  useEffect(() => {
    if (alchemyOauthPrepared) {
      // Add a small delay before completely removing the skeleton for smooth transition
      const timer = setTimeout(() => {
        setShowSkeleton(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(true);
    }
  }, [alchemyOauthPrepared]);

  useEffect(() => {
    console.log({ alchemyOauthPrepared });
    const checkAndPrepareOauth = async () => {
      if (!alchemyOauthPrepared && isOpen) {
        try {
          if (alchSigner) {
            await alchSigner.preparePopupOauth();
            setAlchemyOauthPrepared(true);
          }
        } catch (error) {
          console.error("Error preparing OAuth:", error);
        }
      }
    };

    checkAndPrepareOauth();
  }, [alchemyOauthPrepared, alchSigner, isOpen, setAlchemyOauthPrepared]);

  //connectkit connection handling
  useEffect(() => {
    if (justConnected) {
      if (connector) {
        useAuthStore.setState({ walletIcon: connector.icon, walletName: connector.name });
        useAuthStore.setState({ justConnected: false });
        (async () => {
          try {
            const provider = await connector.getProvider();
            const signer = getEoaOrScaSigner("eoa", provider);
            console.log({ provider, signer, connector });

            if (!signer) {
              console.error("Signer not found for conneckit");
              return;
            }

            setSelectedView(connector.name);
            const connectorIcon = connector.icon || WALLECTCONNECT_ICON;

            await handleIxAuth(
              signer,
              { type: "wallet", connector, connectorIcon, connectorId: connector.id },
              connectorIcon,
            );
          } catch (error) {
            console.error("Error during wallet connection:", error);
            disconnect();
            useAuthStore.setState({ justConnected: false });
          }
        })();
      }
    }
  }, [justConnected, connector, handleIxAuth, setSelectedView, disconnect]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal>
      <DialogContent
        size="3xl"
        background="secondary"
        padding="sm"
        position="center-mobile-bottom"
        hideCloseButton={true}
        height="auto"
        className="!py-0 !pb-6 !rounded-5xl gap-6 max-md:!rounded-b-none max-h-[90vh] md:max-h-[80vh] overflow-hidden transition-all duration-500 ease-in-out flex flex-col"
        onPointerDownOutside={e => {
          if (selectedView !== "walletConnect") e.preventDefault();
        }}
      >
        <div className="relative w-full h-full flex-1 flex-shrink min-h-0 overflow-hidden">
          {/* Skeleton layer */}
          {showSkeleton && (
            <div
              className={`absolute inset-0 z-10 transition-opacity duration-500 ease-in-out ${
                alchemyOauthPrepared ? "opacity-0" : "opacity-100"
              }`}
            >
              <SignInModalSkeleton />
            </div>
          )}

          {/* Actual content layer */}
          <div
            className={`relative z-20 transition-opacity duration-500 ease-in-out ${
              alchemyOauthPrepared ? "opacity-100" : "opacity-0"
            }`}
          >
            <MobileView />
            <DesktopView />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
